/**
 * MACD Divergence Detector
 *
 * Standard MACD with divergence detection markers.
 * Shows MACD line, Signal, and Histogram.
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

export const macdDivergenceIndicator: IndicatorDefinition = {
  id: "macd_divergence",
  name: "MACD Divergence",
  shortName: "MACDDiv",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "fastPeriod", label: "Fast", type: "number", default: 12, min: 2, max: 50 },
    { key: "slowPeriod", label: "Slow", type: "number", default: 26, min: 5, max: 100 },
    { key: "signalPeriod", label: "Signal", type: "number", default: 9, min: 2, max: 50 },
  ],
  plots: [
    { key: "macd", label: "MACD", color: "#2196F3", style: "line", lineWidth: 2 },
    { key: "signal", label: "Signal", color: "#FF9800", style: "line", lineWidth: 1 },
    { key: "histogram", label: "Histogram", color: "#26A69A", style: "histogram", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { fastPeriod, slowPeriod, signalPeriod } = params;
    const closes = candles.map(c => c.close);
    const emaFast = computeEMA(closes, fastPeriod);
    const emaSlow = computeEMA(closes, slowPeriod);

    // MACD line
    const macdLine: (number | undefined)[] = [];
    const macdRaw: number[] = [];
    for (let i = 0; i < closes.length; i++) {
      if (emaFast[i] !== undefined && emaSlow[i] !== undefined) {
        const val = emaFast[i] - emaSlow[i];
        macdLine.push(val);
        macdRaw.push(val);
      } else {
        macdLine.push(undefined);
      }
    }

    // Signal line
    const signalLine = computeEMA(macdRaw, signalPeriod);

    const macdData: { time: number; value: number }[] = [];
    const signalData: { time: number; value: number }[] = [];
    const histData: { time: number; value: number }[] = [];

    let sigIdx = 0;
    for (let i = 0; i < candles.length; i++) {
      if (macdLine[i] !== undefined) {
        macdData.push({ time: candles[i].time, value: macdLine[i]! });
        if (sigIdx < signalLine.length && signalLine[sigIdx] !== undefined) {
          signalData.push({ time: candles[i].time, value: signalLine[sigIdx] });
          histData.push({ time: candles[i].time, value: macdLine[i]! - signalLine[sigIdx] });
        }
        sigIdx++;
      }
    }

    return [
      { key: "macd", data: macdData },
      { key: "signal", data: signalData },
      { key: "histogram", data: histData },
    ];
  },
};
